import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ApiService } from 'src/app/services/api.service';
import { ApexAxisChartSeries, ApexChart, ApexPlotOptions, ApexStroke, ChartComponent } from 'ng-apexcharts';
import { HoursToHourStringPipe } from 'src/app/pipes/hours-to-hour-string.pipe';
import { MetricCardComponent } from 'src/app/components/main/shared/metric-card/metric-card.component';
import {
  KpiEvaluationsResponse,
  CompanyProductKpiEvaluationsGroup,
  ProductionKeyPerformanceIndicatorEvaluation,
  ProductionKeyPerformanceIndicatorEvaluationWithData,
  getKpiEvaluationStyle,
  extractTooltipSummary,
} from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { ProductionKeyPerformanceIndicatorEvaluationEnum } from 'src/app/enums/production-key-performance-indicator-evaluation.enum';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { KpiEvaluationPreviewDialogComponent } from 'src/app/components/main/employees/performance/my-metrics/kpi-evaluation-preview-dialog/kpi-evaluation-preview-dialog.component';
import {
  MonthNavigationComponent,
  formatMonthKeyFromDate,
  formatMonthLabel,
  getNextMonthKey,
  getPreviousMonthKey,
  normalizeMonthKey,
} from 'src/app/components/main/shared/month-navigation/month-navigation.component';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';

export interface UserMetrics {
  today_expected_hours: number;
  today_registered_hours: number;
  today_clocked_hours: number;
  weekly_registered_hours: number;
  weekly_expected_hours: number;
  weekly_percentage: number;
  next_vacation_days: number | null;
}

type RadialChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  plotOptions: ApexPlotOptions;
  colors: string[];
  stroke: ApexStroke;
};

type KpiCategoryFilter = 'all' | 'basic' | 'company_product';

interface BasicKpiCard {
  id: number | string;
  name: string;
  statusColor: string;
  tooltip: string;
  rawData: ProductionKeyPerformanceIndicatorEvaluationWithData;
}

interface CompanyProductKpiCard {
  id: string;
  metricName: string;
  statusColor: string;
  tooltip: string;
  rawData: ProductionKeyPerformanceIndicatorEvaluationWithData;
}

interface CompanyProductKpiGroup {
  id: string;
  companyProductName: string;
  companyProductLogoUrl: string;
  cards: CompanyProductKpiCard[];
  myCompanyProductCards: CompanyProductKpiCard[];
}

@Component({
  selector: 'app-my-metrics',
  imports: [
    ChartComponent,
    HoursToHourStringPipe,
    MetricCardComponent,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MonthNavigationComponent,
  ],
  templateUrl: './my-metrics.component.html',
  styleUrl: './my-metrics.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyMetricsComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });
  private readonly current_month_key = formatMonthKeyFromDate(new Date());

  readonly can_go_to_next_month = computed((): boolean => this.selectedMonth() < this.current_month_key);
  readonly can_go_to_current_month = computed((): boolean => this.selectedMonth() !== this.current_month_key);

  metrics = toSignal(
    this.api.getMyDayMetrics().pipe(
      map(
        (data: UserMetrics): UserMetrics => ({
          today_expected_hours: data.today_expected_hours,
          today_clocked_hours: data.today_clocked_hours,
          today_registered_hours: data.today_registered_hours,
          weekly_registered_hours: data.weekly_registered_hours,
          weekly_expected_hours: data.weekly_expected_hours,
          weekly_percentage: data.weekly_percentage,
          next_vacation_days: data.next_vacation_days,
        })
      ),
      catchError(() => of<UserMetrics | null>(null))
    ),
    { initialValue: null as UserMetrics | null }
  );

  selectedMonth = computed((): string => {
    const queryMonth = this.queryParams().get('month');
    return normalizeMonthKey(queryMonth) ?? this.current_month_key;
  });

  selectedMonthLabel = computed((): string => formatMonthLabel(this.selectedMonth()));

  goToPreviousMonth(): void {
    this.updateMonthQueryParam(getPreviousMonthKey(this.selectedMonth()));
  }

  goToNextMonth(): void {
    this.updateMonthQueryParam(getNextMonthKey(this.selectedMonth()));
  }

  goToCurrentMonth(): void {
    this.updateMonthQueryParam(this.current_month_key);
  }

  private updateMonthQueryParam(month: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        month: month === this.current_month_key ? null : month,
      },
      queryParamsHandling: 'merge',
    });
  }

  kpiEvaluationsResponse = toSignal(
    toObservable(this.selectedMonth).pipe(
      switchMap((month: string): Observable<KpiEvaluationsResponse> => {
        const params = new HttpParams().set('month', month);

        return this.api.getMyKpiEvaluations(params).pipe(
          catchError((): Observable<KpiEvaluationsResponse> => {
            return of<KpiEvaluationsResponse>({
              evaluations: [],
              company_product_groups: [],
              start_date: '',
              end_date: '',
            });
          })
        );
      })
    ),
    {
      initialValue: {
        evaluations: [],
        company_product_groups: [],
        start_date: '',
        end_date: '',
      } as KpiEvaluationsResponse,
    }
  );

  selectedKpiCategory = signal<KpiCategoryFilter>('all');

  categoryFilters: Array<{ label: string; value: KpiCategoryFilter }> = [
    { label: 'Todas', value: 'all' },
    { label: 'Básicas', value: 'basic' },
    { label: 'Producto', value: 'company_product' },
  ];

  basicKpiCards = computed<BasicKpiCard[]>(() => {
    return (this.kpiEvaluationsResponse().evaluations ?? []).map((kpiEvaluation, index) => {
      const selectedEvaluation = this.getEvaluationAtSelectedMonth(kpiEvaluation);
      const style = getKpiEvaluationStyle(selectedEvaluation?.evaluation ?? ProductionKeyPerformanceIndicatorEvaluationEnum.NotCalculable);
      const indicator = this.getKpiIndicator(kpiEvaluation);

      return {
        id: indicator?.id ?? `kpi-${index}`,
        name: indicator?.name ?? 'Métrica',
        statusColor: style.color,
        tooltip: this.buildKpiTooltip(selectedEvaluation, kpiEvaluation.has_exception),
        rawData: kpiEvaluation,
      };
    });
  });

  companyProductKpiGroups = computed<CompanyProductKpiGroup[]>(() => {
    const response = this.kpiEvaluationsResponse();
    const explicitGroups = response.company_product_groups ?? [];
    if (explicitGroups.length > 0) {
      return explicitGroups.map((companyProductGroup: CompanyProductKpiEvaluationsGroup) => {
        const companyProductCards = companyProductGroup.company_product_kpis.map((kpiEvaluation, index) =>
          this.mapCompanyProductCard(kpiEvaluation, 'company_product', index)
        );
        const myCompanyProductCards = companyProductGroup.my_company_product_kpis.map((kpiEvaluation, index) =>
          this.mapCompanyProductCard(kpiEvaluation, 'my-company_product', index)
        );

        return {
          id: `company-product-${companyProductGroup.company_product.id}`,
          companyProductName: companyProductGroup.company_product.name,
          companyProductLogoUrl: companyProductGroup.company_product.logo_url,
          cards: companyProductCards,
          myCompanyProductCards,
        };
      });
    }

    const legacyEvaluations = (response.evaluations ?? []).filter(
      evaluation => evaluation.company_product !== null && evaluation.company_product_id !== null
    );
    const companyProductMap = new Map<number, CompanyProductKpiGroup>();

    for (const evaluation of legacyEvaluations) {
      const company_product = evaluation.company_product;
      const indicatorType = evaluation.production_key_performance_indicator?.type;
      if (!company_product || !indicatorType) {
        continue;
      }

      const existingGroup = companyProductMap.get(company_product.id) ?? {
        id: `company-product-${company_product.id}`,
        companyProductName: company_product.name,
        companyProductLogoUrl: company_product.logo_url,
        cards: [],
        myCompanyProductCards: [],
      };

      const companyProductCard = this.mapCompanyProductCard(
        evaluation,
        indicatorType === 'company_product_personal' ? 'my-company_product' : 'company_product',
        existingGroup.cards.length + existingGroup.myCompanyProductCards.length
      );

      if (indicatorType === 'company_product_personal') {
        existingGroup.myCompanyProductCards.push(companyProductCard);
      } else if (indicatorType === 'company_product') {
        existingGroup.cards.push(companyProductCard);
      }

      companyProductMap.set(company_product.id, existingGroup);
    }

    return Array.from(companyProductMap.values());
  });

  shouldShowBasicMetrics = computed((): boolean => {
    const currentFilter = this.selectedKpiCategory();
    return currentFilter === 'all' || currentFilter === 'basic';
  });

  shouldShowCompanyProductMetrics = computed((): boolean => {
    const currentFilter = this.selectedKpiCategory();
    return currentFilter === 'all' || currentFilter === 'company_product';
  });

  hasCompanyProductMetrics = computed((): boolean => this.companyProductKpiGroups().length > 0);
  hasBasicMetrics = computed((): boolean => this.basicKpiCards().length > 0);
  hasAnyMetrics = computed((): boolean => {
    const showingBasic = this.shouldShowBasicMetrics() && this.hasBasicMetrics();
    const showingCompanyProduct = this.shouldShowCompanyProductMetrics() && this.hasCompanyProductMetrics();
    return showingBasic || showingCompanyProduct;
  });

  radialChartOptions = computed((): RadialChartOptions | null => {
    const metrics = this.metrics();
    if (!metrics) return null;

    return {
      series: [metrics.weekly_percentage] as unknown as ApexAxisChartSeries,
      chart: {
        height: 100,
        type: 'radialBar',
        fontFamily: 'Gilroy, sans-serif',
      },
      plotOptions: {
        radialBar: {
          hollow: { size: '35%' },
          dataLabels: {
            show: true,
            name: { show: false },
            value: {
              offsetY: 8,
              fontSize: '20px',
              fontWeight: 850,
              color: '#000',
              formatter: (val: number): string => Math.round(val) + '%',
            },
          },
          track: { background: '#f2f2f2' },
        },
      },
      colors: ['#0088ff'],
      stroke: { lineCap: 'round' },
    };
  });

  setKpiCategoryFilter(category: KpiCategoryFilter): void {
    this.selectedKpiCategory.set(category);
  }

  openKpiCardPreviewDialog(kpiData: ProductionKeyPerformanceIndicatorEvaluationWithData): void {
    const selectedMonthData = this.buildDialogKpiData(kpiData);
    this.dialog.open(KpiEvaluationPreviewDialogComponent, {
      data: { kind: 'data', evaluation: selectedMonthData },
      panelClass: 'kpi-preview-dialog-panel',
      autoFocus: false,
    });
  }

  formatHours(hours: number): string {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  }

  private getKpiIndicator(kpiEvaluation: ProductionKeyPerformanceIndicatorEvaluationWithData): { id?: number; name?: string } | undefined {
    const evaluation = kpiEvaluation as ProductionKeyPerformanceIndicatorEvaluationWithData & {
      production_key_performance_indicator?: { id?: number; name?: string };
    };
    return evaluation.production_key_performance_indicator ?? evaluation.production_key_performance_indicator;
  }

  private mapCompanyProductCard(
    kpiEvaluation: ProductionKeyPerformanceIndicatorEvaluationWithData,
    prefix: string,
    index: number
  ): CompanyProductKpiCard {
    const selectedEvaluation = this.getEvaluationAtSelectedMonth(kpiEvaluation);
    const style = getKpiEvaluationStyle(selectedEvaluation?.evaluation ?? ProductionKeyPerformanceIndicatorEvaluationEnum.NotCalculable);
    const indicator = this.getKpiIndicator(kpiEvaluation);

    return {
      id: `${prefix}-${kpiEvaluation.company_product_id ?? 'none'}-${indicator?.id ?? index}`,
      metricName: indicator?.name ?? 'Métrica',
      statusColor: style.color,
      tooltip: this.buildKpiTooltip(selectedEvaluation, kpiEvaluation.has_exception),
      rawData: kpiEvaluation,
    };
  }

  private getEvaluationAtSelectedMonth(
    kpiEvaluation: ProductionKeyPerformanceIndicatorEvaluationWithData
  ): ProductionKeyPerformanceIndicatorEvaluation | null {
    return kpiEvaluation.data?.[0] ?? null;
  }

  private buildKpiTooltip(selectedEvaluation: ProductionKeyPerformanceIndicatorEvaluation | null, hasException: boolean): string {
    const style = getKpiEvaluationStyle(selectedEvaluation?.evaluation ?? ProductionKeyPerformanceIndicatorEvaluationEnum.NotCalculable);

    if (hasException) {
      return 'Tienes una excepción activa en este periodo.';
    }

    if (!selectedEvaluation) {
      return 'Sin datos todavía.';
    }
    const backendTooltip = extractTooltipSummary(selectedEvaluation)?.trim();
    if (backendTooltip) {
      return `${style.label}: ${backendTooltip}`;
    }

    if (selectedEvaluation.percentage === null) {
      return 'No calculable en este periodo.';
    }

    return `${Math.round(selectedEvaluation.percentage)}% (${style.label.toLowerCase()}).`;
  }

  private buildDialogKpiData(
    kpiData: ProductionKeyPerformanceIndicatorEvaluationWithData
  ): ProductionKeyPerformanceIndicatorEvaluationWithData {
    return kpiData;
  }
}
